import { Button, DatePicker, Divider, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useState } from 'react';
import { countriesList } from '../../../../../helpers/enumsHelper';
import { statesBrazil } from '../../../../../utils/statesBrazil';
import { searchPostalCode } from '../../../../../utils/searchPostalCode';
import './Company.scss';

const { Option } = Select;

const Company = ({ data, onSave, onDiscard, setUnsavedChanges, setDataToSave }) => {
  const [enableEdit, setEnableEdit] = useState(false);
  const [localData, setLocalData] = useState(data);

  React.useEffect(() => {
    setLocalData(data);
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const [field, nestedField] = name.split('.');
    setLocalData(prevData => {
      const newData = nestedField
        ? {
          ...prevData,
          company: {
            ...prevData.company,
            address: {
              ...prevData.company?.address,
              [nestedField]: value
            }
          }
        }
        : { ...prevData, company: { ...prevData.company, [field]: value } };
      setUnsavedChanges(true);
      return newData;
    });
  };

  const handleOpeningDateChange = (value) => {
    setLocalData(prevData => {
      const newData = {
        ...prevData,
        company: {
          ...prevData.company,
          opening_date: value
        }
      };
      setUnsavedChanges(true);
      return newData;
    });
  };

  const handleSelectChange = (value, name) => {
    const nestedField = name.split('.')[1];
    setLocalData(prevData => {
      const newData = {
        ...prevData,
        company: {
          ...prevData.company,
          address: {
            ...prevData.company?.address,
            [nestedField]: value
          }
        }
      };
      setUnsavedChanges(true);
      return newData;
    });
  };

  const fillAddressFromCep = (cepData) => {
    if (!cepData || cepData.erro) return;
    setLocalData(prevData => {
      const newData = {
        ...prevData,
        company: {
          ...prevData.company,
          address: {
            ...prevData.company?.address,
            street: cepData.logradouro || prevData.company?.address?.street,
            neighborhood: cepData.bairro || prevData.company?.address?.neighborhood,
            city: cepData.localidade || prevData.company?.address?.city,
            state: cepData.uf || prevData.company?.address?.state
          }
        }
      };
      setUnsavedChanges(true);
      return newData;
    });
  };

  const handleCepChange = (e) => {
    handleInputChange(e);
    searchPostalCode(e.target.value, fillAddressFromCep);
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(localData);
    }
    setEnableEdit(false);
    setUnsavedChanges(false);
  };

  const handleDiscard = async () => {
    if (onDiscard) {
      await onDiscard();
    }
    setLocalData(data);
    setEnableEdit(false);
    setUnsavedChanges(false);
  };

  const handleDataToSave = useCallback((data) => {
    setDataToSave(data);
  }, [setDataToSave]);

  React.useEffect(() => {
    if (localData) {
      handleDataToSave(localData);
    }
  }, [localData, handleDataToSave]);

  function formatCNPJ(cnpj) {
    cnpj = cnpj.replace(/\D/g, '');
    cnpj = cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return cnpj;
  }

  return (
    <div className='Company'>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p className='subtitle-page'>Empresa</p>
        <Button
          onClick={() => setEnableEdit(!enableEdit)}
          type={`${enableEdit ? "primary" : ""}`}
        >
          {enableEdit ? "Cancelar" : "Editar"}
        </Button>
      </div>
      <Divider />
      <div className="container-inputs">
        <div className="left-inputs">
          <Form layout="vertical">
            <Form.Item label="Nome">
              <Input
                name="name"
                disabled={!enableEdit}
                placeholder='Nome'
                value={localData?.company?.name || ""}
                onChange={handleInputChange}
              />
            </Form.Item>
            <Form.Item label="Email">
              <Input
                name="email"
                disabled={!enableEdit}
                placeholder='Email'
                value={localData?.company?.email || ""}
                onChange={handleInputChange}
              />
            </Form.Item>
          </Form>
        </div>

        <div className="right-inputs">
          <Form layout="vertical">
            <Form.Item label="Telefone">
              <Input
                name="phone"
                disabled={!enableEdit}
                placeholder='Telefone'
                value={localData?.company?.phone || ""}
                onChange={handleInputChange}
              />
            </Form.Item>
            <Form.Item label="CNPJ">
              <Input
                name="tax_id"
                disabled={!enableEdit}
                placeholder='CNPJ'
                value={formatCNPJ(localData?.company?.tax_id || "")}
                onChange={handleInputChange}
              />
            </Form.Item>
            <Form.Item label="Data de Abertura">
              <DatePicker
                name="opening_date"
                id='opening_date'
                format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                style={{ width: '100%' }}
                disabled={!enableEdit}
                placeholder='DD/MM/AAAA'
                value={localData?.company?.opening_date ? dayjs(localData.company.opening_date) : null}
                onChange={(date) => handleOpeningDateChange(date ? date.format('YYYY-MM-DD') : null)}
              />
            </Form.Item>
          </Form>
        </div>
      </div>
      <Divider />
      <p className='subtitle-page'>Endereço</p>
      <div className="container-inputs">
        <div className="left-inputs">
          <Form layout="vertical">
            <Form.Item label="CEP">
              <Input
                name="address.postal_code"
                disabled={!enableEdit}
                placeholder='CEP'
                value={localData?.company?.address?.postal_code || ""}
                onChange={handleCepChange}
              />
            </Form.Item>
            <Form.Item label="Número">
              <Input
                name="address.number"
                disabled={!enableEdit}
                placeholder='Número'
                value={localData?.company?.address?.number || ""}
                onChange={handleInputChange}
              />
            </Form.Item>
            <Form.Item label="Bairro">
              <Input
                name="address.neighborhood"
                disabled={!enableEdit}
                placeholder='Bairro'
                value={localData?.company?.address?.neighborhood || ""}
                onChange={handleInputChange}
              />
            </Form.Item>
            <Form.Item label="Estado">
              <Select
                name="address.state"
                disabled={!enableEdit}
                placeholder='Selecione o Estado'
                value={localData?.company?.address?.state || ""}
                onChange={(value) => handleSelectChange(value, 'address.state')}
              >
                {Object.entries(statesBrazil).map(([key, label]) => (
                  <Option key={key} value={key}>{label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </div>

        <div className="right-inputs">
          <Form layout="vertical">
            <Form.Item label="Rua">
              <Input
                name="address.street"
                disabled={!enableEdit}
                placeholder='Rua'
                value={localData?.company?.address?.street || ""}
                onChange={handleInputChange}
              />
            </Form.Item>
            <Form.Item label="Complemento">
              <Input
                name="address.complement"
                disabled={!enableEdit}
                placeholder='Complemento'
                value={localData?.company?.address?.complement || ""}
                onChange={handleInputChange}
              />
            </Form.Item>
            <Form.Item label="Cidade">
              <Input
                name="address.city"
                disabled={!enableEdit}
                placeholder='Cidade'
                value={localData?.company?.address?.city || ""}
                onChange={handleInputChange}
              />
            </Form.Item>
            <Form.Item label="País">
              <Select
                name="address.country_code"
                disabled={!enableEdit}
                placeholder='País'
                value={localData?.company?.address?.country_code || ""}
                onChange={(value) => handleSelectChange(value, 'address.country_code')}
              >
                {Object.entries(countriesList).map(([key, label]) => (
                  <Option key={key} value={key}>{label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </div>
      </div>
      {enableEdit &&
        <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={handleSave} type="primary">Salvar</Button>
          <Button onClick={handleDiscard}>Descartar</Button>
        </div>
      }
    </div>
  );
};

export default Company;
